import { z } from "zod";
import { cursorPaginatedResponseSchema, DateSchema, IdSchema, NameSchema } from "./commonSchema";
import type { CursorPaginatedResponse } from "@/types/common";

export const PreferenceTypeSchema = z.enum(["telegram_report"], {
  errorMap: () => ({ message: "errors.validation.preference.type.invalid" }),
});

export type PreferenceType = z.infer<typeof PreferenceTypeSchema>;

export const ReportedUserSchema = z.object({
  userId: IdSchema,
  userName: NameSchema,
  telegramChatId: z.number(),
  enabled: z.boolean(),
  linkedAt: DateSchema,
});

export type ReportedUser = z.infer<typeof ReportedUserSchema>;

export const TelegramReportValueSchema = z.object({
  language: z.enum(["ar", "en", "fr"], {
    errorMap: () => ({ message: "errors.validation.preference.language.invalid" }),
  }),
  reportTime: z
    .string({ required_error: "errors.validation.preference.reportTime.required" })
    .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, {
      message: "errors.validation.preference.reportTime.format",
    }),
  reportTimezone: z.string(),
  reportedUsers: z.array(ReportedUserSchema),
});

export type TelegramReportValue = z.infer<typeof TelegramReportValueSchema>;

const PreferenceBase = z.object({
  id: IdSchema,
  workshopId: IdSchema,
  createdBy: IdSchema,
  createdAt: DateSchema,
  deletedAt: DateSchema.nullable(),
});

export const PreferenceSchema = z.discriminatedUnion("type", [
  PreferenceBase.merge(z.object({
    type: z.literal("telegram_report"),
    value: TelegramReportValueSchema,
  })),
], {
  errorMap: () => ({ message: "errors.validation.preference.type.invalid" }),
});

export type Preference = z.infer<typeof PreferenceSchema>;

export const CreatePreferenceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("telegram_report"),
  }).merge(TelegramReportValueSchema.pick({
    language: true,
    reportTime: true,
  })),
], {
  errorMap: () => ({ message: "errors.validation.preference.type.required" }),
});

export type CreatePreference = z.infer<typeof CreatePreferenceSchema>;

export const UpdateTelegramReportSchema = z.object({
  language: z.enum(["ar", "en", "fr"], {
    errorMap: () => ({ message: "errors.validation.preference.language.invalid" }),
  }),
  reportTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, {
    message: "errors.validation.preference.reportTime.format",
  }),
});

export type UpdateTelegramReport = z.infer<typeof UpdateTelegramReportSchema>;

export const EnrollResponseSchema = z.object({
  deepLink: z.string(),
});

export type EnrollResponse = z.infer<typeof EnrollResponseSchema>;

export const SendNowResponseSchema = z.object({
  recipients: z.number(),
});

export type SendNowResponse = z.infer<typeof SendNowResponseSchema>;

export type PreferenceListResponse = CursorPaginatedResponse<Preference>;

export const PreferenceListResponseSchema = cursorPaginatedResponseSchema(PreferenceSchema);
